import * as React from 'react'
import { GridColumnVisibilityModel, GridDensity } from '@mui/x-data-grid-pro'
import { isValidEmail } from '@sales-center/common'
import {
  ButtonWithTooltip,
  HighlightedText,
  MoreButton,
} from '@sales-center/components'
import { ILocationSearchModel } from '@user/query/useDuplicateLocationSearch'
import { permissionDenied } from '@user/store/authorization'
import Box from '@mui/material/Box'

interface Props {
  onClickManage?: (action: string, duplicate: ILocationSearchModel) => void
  densityMode?: GridDensity
  highlights?: Record<string, string>
  isGuestUser?: boolean
  canAddLeads?: boolean
  canCreateOpps?: boolean
}

export const pestPacLocationsDuplicatesColumns = ({
  onClickManage,
  densityMode,
  highlights = {},
  isGuestUser = false,
  canAddLeads = false,
  canCreateOpps = false,
}: Props) => {
  return [
    {
      width: !isGuestUser ? 330 : 150,
      minWidth: !isGuestUser ? 330 : 150,
      maxWidth: !isGuestUser ? 330 : 150,
      type: 'actions',
      field: 'actions',
      headerName: 'Actions',
      renderHeader: () => null,
      sortable: false,
      resizable: false,
      filterable: false,
      disableReorder: true,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Box
          display="flex"
          alignItems="center"
          flexWrap="nowrap"
          gap={!isGuestUser ? 2 : undefined}
        >
          <MoreButton
            dataTestId={`duplicateLocationMoreBtn-${row.id}`}
            options={[
              {
                label: 'View Location in PestPac',
                onClick: () => onClickManage && onClickManage('view', row),
                dataTestId: 'view-location-in-pestpac',
              },
            ]}
          />

          {!isGuestUser && (
            <ButtonWithTooltip
              variant="outlined"
              color="primary"
              size={densityMode === 'compact' ? 'small' : 'medium'}
              style={{ whiteSpace: 'nowrap', minWidth: 'unset' }}
              onClick={() => onClickManage && onClickManage('opportunity', row)}
              disabled={!canCreateOpps}
              showTooltip={!canCreateOpps}
              tooltipTitle={permissionDenied}
              data-test-id={`create-opportunity-from-duplicate-button-${row.id}`}
            >
              Create Opportunity
            </ButtonWithTooltip>
          )}

          <ButtonWithTooltip
            variant="outlined"
            color="primary"
            size={densityMode === 'compact' ? 'small' : 'medium'}
            style={{ whiteSpace: 'nowrap', minWidth: 'unset' }}
            onClick={() => onClickManage && onClickManage('lead', row)}
            disabled={!canAddLeads}
            showTooltip={!canAddLeads}
            tooltipTitle={permissionDenied}
            data-test-id={`add-lead-from-duplicate-button-${row.id}`}
          >
            Add Lead
          </ButtonWithTooltip>
        </Box>
      ),
    },
    {
      field: 'firstName',
      headerName: 'Service Location Contact Name',
      width: 250,
      renderCell: ({ row }) => (
        <>
          <HighlightedText
            text={row.firstName}
            searchText={highlights['firstName'] ?? ''}
          />
          &nbsp;
          <HighlightedText
            text={row.lastName}
            searchText={highlights['lastName'] ?? ''}
          />
        </>
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 350,
      renderCell: ({ row }) => (
        <HighlightedText
          searchText={highlights['address'] ?? ''}
          text={`${row.address.address} ${row.address.city} ${row.address.state} ${row.address.postalCode}`.trim()}
        />
      ),
    },
    {
      field: 'company',
      headerName: 'Company Name',
      width: 250,
      renderCell: ({ row }) => (
        <HighlightedText
          searchText={highlights['company'] ?? ''}
          text={row.company}
        />
      ),
    },
    {
      field: 'branch',
      headerName: 'Branch',
      width: 250,
      renderCell: ({ row }) => (
        <HighlightedText
          searchText={highlights['branch'] ?? ''}
          text={row.branch}
        />
      ),
    },
    {
      field: 'email',
      headerName: 'Email Address',
      width: 300,
      renderCell: ({ row }) =>
        isValidEmail(row.email) ? (
          <a
            href={`mailto:${row.email}`}
            target="_blank"
            rel="noreferrer"
            data-test-id={`email-link-from-duplicate-button-${row.id}`}
          >
            <HighlightedText
              searchText={highlights['email'] ?? ''}
              text={row.email}
            />
          </a>
        ) : (
          <HighlightedText
            searchText={highlights['email'] ?? ''}
            text={row.email}
          />
        ),
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      width: 250,
      renderCell: ({ row }) => (
        <HighlightedText
          searchText={highlights['phone'] ?? ''}
          text={row.phone}
        />
      ),
    },
  ]
}

export const pestPacLocationsDuplicatesDefaultColVis =
  pestPacLocationsDuplicatesColumns({}).reduce<GridColumnVisibilityModel>(
    (acc, { field }) => {
      if (acc[field] === undefined) {
        return { ...acc, [field]: true }
      } else return acc
    },
    {},
  )

export const pestPacLocationsDuplicatesDefaultColumns =
  pestPacLocationsDuplicatesColumns({}).map((c) => c.field)
